import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useMultilingualContent } from '@/hooks/useMultilingualContent';

const CONTACT_KEYS = [
  "contact_title",
  "contact_title_accent",
  "contact_description",
  "contact_name_placeholder",
  "contact_email_placeholder",
  "contact_message_placeholder",
  "contact_button_text",
] as const;

const DEFAULT_CONTENT = {
  contact_title: "Entre em",
  contact_title_accent: "Contato",
  contact_description: "Envie sua mensagem e responderei o mais breve possível!",
  contact_name_placeholder: "Seu nome",
  contact_email_placeholder: "seu@email.com",
  contact_message_placeholder: "Digite sua mensagem aqui...",
  contact_button_text: "Enviar Mensagem",
};

type ContactContent = typeof DEFAULT_CONTENT;

export const useContactContent = () => {
  const { i18n } = useTranslation();
  const { transformContent } = useMultilingualContent();
  const [content, setContent] = useState<ContactContent>(DEFAULT_CONTENT);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase
        .from("site_content")
        .select("content_key, content_value, content_value_en, content_value_es")
        .in("content_key", CONTACT_KEYS);

      if (data) {
        const transformedData = transformContent(data);
        const contentMap = { ...DEFAULT_CONTENT };
        
        // transformContent returns Record<string, string> already
        CONTACT_KEYS.forEach((key) => {
          if (transformedData[key] && transformedData[key].trim() !== '') {
            contentMap[key] = transformedData[key];
          }
        });
        
        setContent(contentMap);
        setIsLoaded(true);
      }
    };

    fetchContent();

    // Real-time subscription
    const channel = supabase
      .channel(`contact-content-changes-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "site_content",
          filter: `content_key=in.(${CONTACT_KEYS.join(",")})`,
        },
        () => {
          fetchContent();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [i18n.language]);

  return { content, isLoaded };
};
